import type { DashboardSearch } from '@/types/dashboard';

// Die Zähler-Chips in der Fußzeile (#77, A20).
//
// DER BEFUND
//   Sie setzten je EINEN weiteren Filter. Stand oben schon einer,
//   wirkten beide zusammen — und man musste die Filter oben von Hand
//   herausnehmen.
//
//   Der Kern ist die Zahl. Sie wird UNGEFILTERT gerechnet, weil die
//   Fußzeile den Arbeitsvorrat des Ladens beschreibt und nicht den
//   Ausschnitt, den man gerade sieht. Beim Addieren widersprechen sich
//   Auskunft und Ergebnis: man liest „12 von dir", tippt, und sieht
//   vier Zeilen.
//
// WAS EIN CHIP STEHEN LÄSST — und warum genau das
//   Nachgemessen am Backend (api/lib/dashboard.php, `dashBuildWhere`):
//   `q` und `quelle` gelten in BEIDEN Modi, `list` wie `aggregate`.
//   Alles andere — Zustände, Erfasser, Verlag, Versandart, Alter,
//   „nur meine", „nur überfällig", „Beschaffung offen" — wirkt
//   ausschließlich auf die Liste, nicht auf die Zählung.
//
//   Daraus folgt die Regel von selbst: Ein Chip darf genau das stehen
//   lassen, was seine eigene Zahl mitgerechnet hat. Der Suchbegriff und
//   der Reiter bleiben, weil die Zahl sie berücksichtigt; alles andere
//   fällt weg, weil sie es nicht tut. Bliebe eines davon stehen, wäre
//   die Zahl auf dem Chip wieder etwas anderes als die Zeilenzahl
//   darunter — und genau das war der Befund.
//
//   `sort` und `neu` sind keine Filter: die eine Angabe ordnet, die
//   andere öffnet ein Fenster. Beide bleiben unberührt.

/** Was ein Chip-Tipp stehen lässt. */
export const CHIP_BEHAELT = ['q', 'quelle', 'sort', 'neu'] as const;

/** Alles, was die Liste einschränkt, ohne die Zählung zu berühren. */
export const CHIP_RAEUMT = [
  'zustaende',
  'erfasser',
  'mein',
  'alter_min',
  'alter_max',
  'verlag_id',
  'versandart_id',
  'nur_ueberfaellig',
  'beschaffung_offen',
  'nachlieferung'
] as const;

export type ChipFilter = Partial<DashboardSearch>;
type Suche = DashboardSearch & { neu?: 1 };

/** Zählt ein Wert als gesetzt? Leer, `false` und `undefined` tun es nicht. */
function gesetzt(wert: unknown): boolean {
  return wert !== undefined && wert !== '' && wert !== false && wert !== null;
}

/** Die gerade wirksamen Listen-Filter, ohne die Reihenfolge der Schlüssel. */
export function aktiveFilter(search: Suche): Record<string, unknown> {
  const raus: Record<string, unknown> = {};
  for (const k of CHIP_RAEUMT) {
    const wert = (search as Record<string, unknown>)[k];
    if (gesetzt(wert)) raus[k] = wert;
  }
  return raus;
}

/**
 * Ist dieser Chip gerade der EINZIGE wirksame Filter?
 *
 * Nur dann ist er gedrückt. Steht daneben noch etwas anderes, zeigt die
 * Liste nicht die Zahl des Chips — und ein gedrückter Knopf, der etwas
 * anderes behauptet, ist schlimmer als gar keiner.
 */
export function chipIstAktiv(search: Suche, filter: ChipFilter): boolean {
  const aktiv = aktiveFilter(search);
  const soll = aktiveFilter(filter as Suche);
  const schluessel = Object.keys(soll);
  if (Object.keys(aktiv).length !== schluessel.length) return false;
  return schluessel.every((k) => String(aktiv[k]) === String(soll[k]));
}

/**
 * Die Suche NACH dem Tipp auf einen Chip.
 *
 * Gedrückt → gelöst: zurück zur ungefilterten Liste. Ein Chip ist damit
 * ein Schalter und keine Einbahnstraße.
 *
 * Sonst → dieser Filter, und nur dieser.
 */
export function chipSuche(search: Suche, filter: ChipFilter): Suche {
  const neu: Record<string, unknown> = {};
  for (const k of CHIP_BEHAELT) {
    const wert = (search as Record<string, unknown>)[k];
    if (gesetzt(wert)) neu[k] = wert;
  }
  if (chipIstAktiv(search, filter)) return neu as Suche;
  for (const [k, wert] of Object.entries(filter)) {
    if (gesetzt(wert)) neu[k] = wert;
  }
  return neu as Suche;
}
